/**
 * T18 — Race-end recap (RACE_SCORING phase) — BODY ONLY (PR-4 D3 통합).
 *
 * Shared top frame (titlebar + RoundInfo + TopBanner rail in READY mode) is
 * rendered by GameScreen via <HorrorTopFrame>; this view owns only the staged
 * body + footer:
 *   ① 보드 순위(인게임 <RaceBoardView> 최종 위치 재사용) → ② 비밀 베팅 순차 공개
 *   → ③ 가넷 정산(레더보드 FLIP) → footer(3-stage 탭 + 확인 게이트).
 *
 * 자동 재생이 끝나면 footer의 3-탭이 활성화돼 사용자가 어떤 단계든 다시 볼 수
 * 있다. Per-player 확인 상태는 상단 레일(레디 ✓ 칩)에 흡수됐으므로 자체 헤더 /
 * ConfirmTracker 칩은 모두 제거. 다음 레이스 진입은 서버 권위 게이트(getHostAction
 * + applyAdvanceRace).
 *
 * Design + testid contract: docs/wireframes/horror-race/states/race-recap.html.
 */

import {
  ALL_CHARACTERS,
  type Character,
  calculateGarnetsFromRace,
  type Player,
  type RaceResult,
} from "@cloudnest/horror-race-engine";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { RECAP_CONFIG } from "../recap-config";
import { getHorrorPlayerToken } from "../utils/horror-tokens";
import { ConfirmFooter } from "./board/ConfirmGate";
import { RaceBoardView } from "./board/RaceBoardView";
import { CHARACTER_COLORS, CHARACTER_LABELS } from "./character-display";
import { HorrorCharacterChip } from "./HorrorCharacterChip";
import { HorrorCharacterIcon } from "./HorrorCharacterIcon";
import { type StageTabId, type StageTabState, StageTabs } from "./StageTabs";

const RANK_MEDALS = ["🥇", "🥈", "🥉"];

type Stage = "rankings" | "reveal" | "scoring";

const STAGE_TO_ID: Record<Stage, StageTabId> = {
  rankings: 1,
  reveal: 2,
  scoring: 3,
};
const ID_TO_STAGE: Record<StageTabId, Stage> = {
  1: "rankings",
  2: "reveal",
  3: "scoring",
};

function prefersReducedMotion(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

const wait = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

function buildRecapRunKey(
  result: RaceResult,
  players: readonly Player[],
  garnets: Readonly<Record<string, number>>,
): string {
  const rankings = result.rankings
    .map((r) => `${r.character}:${r.position}:${r.rank}`)
    .join("|");
  const playerTotals = players
    .map(
      (p) =>
        `${p.id}:${p.secretSelection?.join("+") ?? ""}:${garnets[p.id] ?? 0}`,
    )
    .join("|");
  return `${result.raceNumber}:${result.finisher ?? ""}:${rankings}:${playerTotals}`;
}

interface RaceRecapViewProps {
  readonly result: RaceResult;
  readonly players: readonly Player[];
  /** Post-race merged garnet totals (already includes this race's award). */
  readonly garnets: Readonly<Record<string, number>>;
  readonly playerNames: Readonly<Record<string, string>>;
  /** Track length for stage ① board reuse (in-game <RaceBoardView>). */
  readonly trackLength: number;
  readonly myParticipantId: string | null;
  /** Observers watch the recap but have no 확인 button. */
  readonly readOnly?: boolean;
  readonly onConfirm: () => void;
}

export function RaceRecapView({
  result,
  players,
  garnets,
  playerNames,
  trackLength,
  myParticipantId,
  readOnly = false,
  onConfirm,
}: RaceRecapViewProps) {
  // This race's per-player award (engine math — never re-derive by hand) and the
  // pre-race totals it rolls up from.
  const award = calculateGarnetsFromRace(result.rankings, players);
  const finalTotals: Record<string, number> = {};
  const preTotals: Record<string, number> = {};
  for (const p of players) {
    finalTotals[p.id] = garnets[p.id] ?? 0;
    preTotals[p.id] = (garnets[p.id] ?? 0) - (award[p.id] ?? 0);
  }
  const recapRunKey = buildRecapRunKey(result, players, garnets);

  const [stage, setStage] = useState<Stage>("rankings");
  const [revealed, setRevealed] = useState<ReadonlySet<string>>(new Set());
  const [displayed, setDisplayed] =
    useState<Readonly<Record<string, number>>>(preTotals);
  // PR-4 — per-tab autoplay progress (pending/playing/done) + the autoplay-done
  // flag that flips tabs from disabled-progress to clickable navigation.
  const [stageStates, setStageStates] = useState<
    Readonly<Record<StageTabId, StageTabState>>
  >({ 1: "playing", 2: "pending", 3: "pending" });
  const [autoplayDone, setAutoplayDone] = useState(false);

  // Client-driven presentation timeline. Runs once per recap result; player
  // confirmations re-render without restarting it.
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally one run per recap
  useEffect(() => {
    let cancelled = false;
    setStage("rankings");
    setRevealed(new Set());
    setDisplayed(preTotals);
    setStageStates({ 1: "playing", 2: "pending", 3: "pending" });
    setAutoplayDone(false);

    async function play() {
      if (prefersReducedMotion()) {
        setRevealed(new Set(players.map((p) => p.id)));
        setDisplayed(finalTotals);
        setStage("scoring");
        setStageStates({ 1: "done", 2: "done", 3: "done" });
        setAutoplayDone(true);
        return;
      }

      setStage("rankings");
      setStageStates({ 1: "playing", 2: "pending", 3: "pending" });
      await wait(RECAP_CONFIG.rankingHoldMs);
      if (cancelled) return;
      setStageStates({ 1: "done", 2: "playing", 3: "pending" });

      setStage("reveal");
      for (const p of players) {
        await wait(RECAP_CONFIG.revealStepMs);
        if (cancelled) return;
        setRevealed((prev) => new Set([...prev, p.id]));
      }

      await wait(RECAP_CONFIG.stageGapMs);
      if (cancelled) return;
      setStageStates({ 1: "done", 2: "done", 3: "playing" });
      setStage("scoring");
      for (const p of players) {
        await wait(RECAP_CONFIG.garnetStepGapMs);
        if (cancelled) return;
        await rollGarnet(
          p.id,
          preTotals[p.id],
          finalTotals[p.id],
          () => cancelled,
        );
      }
      if (cancelled) return;
      setStageStates({ 1: "done", 2: "done", 3: "done" });
      setAutoplayDone(true);
    }

    function rollGarnet(
      id: string,
      from: number,
      to: number,
      isCancelled: () => boolean,
    ): Promise<void> {
      if (from === to) {
        setDisplayed((prev) => ({ ...prev, [id]: to }));
        return Promise.resolve();
      }
      return new Promise((done) => {
        const t0 = performance.now();
        const tick = (now: number) => {
          if (isCancelled()) return done();
          const p = Math.min(1, (now - t0) / RECAP_CONFIG.garnetRollMs);
          const eased = 1 - (1 - p) ** 3;
          const value = Math.round(from + (to - from) * eased);
          setDisplayed((prev) => ({ ...prev, [id]: value }));
          if (p < 1) requestAnimationFrame(tick);
          else done();
        };
        requestAnimationFrame(tick);
      });
    }

    void play();
    return () => {
      cancelled = true;
    };
  }, [recapRunKey]);

  const confirmedCount = players.filter((p) => p.hasConfirmedRecap).length;
  const allConfirmed = confirmedCount === players.length;
  const myPlayer = players.find((p) => p.id === myParticipantId);
  const iConfirmed = myPlayer?.hasConfirmedRecap ?? false;

  return (
    <>
      {/* STAGE BODY — only one visible at a time, controlled by autoplay or tab click */}
      <div className="relative flex flex-1 flex-col overflow-y-auto p-4">
        {stage === "rankings" && (
          <RankingsStage result={result} trackLength={trackLength} />
        )}
        {stage === "reveal" && (
          <RevealStage
            players={players}
            playerNames={playerNames}
            revealed={revealed}
          />
        )}
        {stage === "scoring" && (
          <ScoringStage
            players={players}
            playerNames={playerNames}
            displayed={displayed}
            award={award}
          />
        )}
      </div>

      <ConfirmFooter
        readOnly={readOnly}
        allConfirmed={allConfirmed}
        iConfirmed={iConfirmed}
        advanceMsg="모든 플레이어가 확인했습니다 — 다음 레이스를 시작합니다…"
        onConfirm={onConfirm}
        btnTestId="recap-confirm-btn"
        advanceMsgTestId="recap-advance-msg"
        topSlot={
          <StageTabs
            activeStage={STAGE_TO_ID[stage]}
            stageStates={stageStates}
            autoplayDone={autoplayDone}
            onTabClick={(id) => setStage(ID_TO_STAGE[id])}
          />
        }
      />
    </>
  );
}

// ── STAGE 1 — board-highlight rankings (PR-4: reuse in-game RaceBoardView) ──
function RankingsStage({
  result,
  trackLength,
}: {
  readonly result: RaceResult;
  readonly trackLength: number;
}) {
  // Per-character final positions for the shared board.
  const positions: Record<Character, number> = {
    VAMPIRE: 0,
    JIANGSHI: 0,
    GUMIHO: 0,
    MUMMY: 0,
    ZOMBIE: 0,
  };
  for (const c of ALL_CHARACTERS) positions[c] = 0;
  for (const r of result.rankings) positions[r.character] = r.position;
  const sorted = [...result.rankings].sort((a, b) => a.rank - b.rank);

  return (
    <section
      data-testid="recap-stage"
      data-stage="rankings"
      className="flex flex-col gap-3"
    >
      <p
        className="text-center text-xs"
        style={{ color: "var(--cn-horror-text-muted)" }}
      >
        이번 레이스{" "}
        <b style={{ color: "var(--cn-horror-round-text)" }}>최종 순위</b> —
        인게임 보드 재사용
      </p>
      <div data-testid="recap-rank-board">
        <RaceBoardView
          positions={positions}
          trackLength={trackLength}
          laneTestId="recap-rank-track-lane"
          pawnTestId="recap-rank-pawn"
        />
      </div>
      <div className="flex flex-col gap-0.5">
        {sorted.map((ranking) => (
          <div
            key={ranking.character}
            data-testid="recap-rank-lane"
            data-character={ranking.character}
            data-rank={ranking.rank}
            className="flex items-center gap-2 px-1 py-0.5 text-xs"
            style={{
              color:
                ranking.rank <= 3
                  ? "var(--cn-horror-text)"
                  : "var(--cn-horror-text-muted)",
            }}
          >
            <span className="min-w-[24px] text-center">
              {ranking.rank <= 3
                ? RANK_MEDALS[ranking.rank - 1]
                : `${ranking.rank}위`}
            </span>
            <HorrorCharacterIcon
              character={ranking.character}
              size={16}
              color={CHARACTER_COLORS[ranking.character]}
            />
            <span>{CHARACTER_LABELS[ranking.character]}</span>
            <span style={{ color: "var(--cn-horror-text-muted)" }}>
              {ranking.position}칸
              {result.finisher === ranking.character ? " · 골인" : ""}
            </span>
          </div>
        ))}
      </div>
    </section>
  );
}

// ── STAGE 2 — sequential secret-bet reveal ────────────────────────────────
function RevealStage({
  players,
  playerNames,
  revealed,
}: {
  readonly players: readonly Player[];
  readonly playerNames: Readonly<Record<string, string>>;
  readonly revealed: ReadonlySet<string>;
}) {
  return (
    <section data-testid="recap-stage" data-stage="reveal">
      <p
        className="mb-3 text-center text-xs"
        style={{ color: "var(--cn-horror-text-muted)" }}
      >
        비밀 베팅{" "}
        <b style={{ color: "var(--cn-horror-round-text)" }}>순차 공개</b>
      </p>
      <div className="flex flex-col gap-2.5">
        {players.map((player, idx) => {
          const isRevealed = revealed.has(player.id);
          return (
            <div
              key={player.id}
              data-testid="recap-reveal-row"
              data-player={player.id}
              data-revealed={isRevealed}
              className="flex items-center gap-2.5 rounded-xl px-3 py-2.5"
              style={{
                backgroundColor: "var(--cn-horror-card-bg)",
                border: isRevealed
                  ? "1px solid var(--cn-horror-round-text)"
                  : "1px solid var(--cn-horror-surface-border)",
              }}
            >
              <span className="flex min-w-[84px] items-center gap-1.5 font-extrabold text-[13px]">
                <span
                  className="inline-block h-2.5 w-2.5 rounded-full"
                  style={{ backgroundColor: getHorrorPlayerToken(idx) }}
                />
                <span style={{ color: "var(--cn-horror-text)" }}>
                  {playerNames[player.id] ?? player.name}
                </span>
              </span>
              {isRevealed && player.secretSelection ? (
                <span className="ml-auto flex gap-2.5">
                  {player.secretSelection.map((character) => (
                    <HorrorCharacterChip
                      key={`${player.id}-${character}`}
                      character={character}
                      iconSize={26}
                      labelSize={10}
                    />
                  ))}
                </span>
              ) : (
                <span
                  className="ml-auto text-[13px] tracking-widest"
                  style={{ color: "var(--cn-horror-text-muted)" }}
                >
                  🔒 ? · ?
                </span>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

// ── STAGE 3 — garnet tally + swapping leaderboard ─────────────────────────
function ScoringStage({
  players,
  playerNames,
  displayed,
  award,
}: {
  readonly players: readonly Player[];
  readonly playerNames: Readonly<Record<string, string>>;
  readonly displayed: Readonly<Record<string, number>>;
  readonly award: Readonly<Record<string, number>>;
}) {
  const seatIndex = new Map(players.map((p, i) => [p.id, i]));
  const ordered = [...players].sort((a, b) => {
    const diff = (displayed[b.id] ?? 0) - (displayed[a.id] ?? 0);
    if (diff !== 0) return diff;
    return (seatIndex.get(a.id) ?? 0) - (seatIndex.get(b.id) ?? 0);
  });

  // FLIP: animate row position changes as the leaderboard re-sorts.
  const rowRefs = useRef(new Map<string, HTMLDivElement>());
  const prevRects = useRef(new Map<string, DOMRect>());
  // biome-ignore lint/correctness/useExhaustiveDependencies: re-measure on every reorder
  useLayoutEffect(() => {
    if (prefersReducedMotion()) return;
    for (const [id, el] of rowRefs.current) {
      const prev = prevRects.current.get(id);
      const next = el.getBoundingClientRect();
      if (prev) {
        const dy = prev.top - next.top;
        if (dy) {
          el.animate(
            [
              { transform: `translateY(${dy}px)` },
              { transform: "translateY(0)" },
            ],
            { duration: 320, easing: "cubic-bezier(.2,.8,.2,1)" },
          );
        }
      }
      prevRects.current.set(id, next);
    }
  }, [ordered.map((p) => p.id).join(",")]);

  return (
    <section data-testid="recap-stage" data-stage="scoring">
      <p
        className="mb-3 text-center text-xs"
        style={{ color: "var(--cn-horror-text-muted)" }}
      >
        <b style={{ color: "var(--cn-horror-round-text)" }}>가넷 정산</b> — 점수
        상승 · 순위 스와핑
      </p>
      <div className="flex flex-col gap-2">
        {ordered.map((player, rank) => {
          const idx = seatIndex.get(player.id) ?? 0;
          const leading = rank === 0;
          const delta = award[player.id] ?? 0;
          return (
            <div
              key={player.id}
              ref={(el) => {
                if (el) rowRefs.current.set(player.id, el);
                else rowRefs.current.delete(player.id);
              }}
              data-testid="recap-leaderboard-row"
              data-player={player.id}
              className="grid items-center gap-2.5 rounded-xl px-3 py-2.5"
              style={{
                gridTemplateColumns: "26px 1fr auto",
                backgroundColor: "var(--cn-horror-card-bg)",
                border: leading
                  ? "1px solid var(--cn-horror-finished-flash)"
                  : "1px solid var(--cn-horror-surface-border)",
                boxShadow: leading ? "0 0 10px rgba(255,215,0,0.3)" : "none",
              }}
            >
              <span
                className="text-center font-extrabold text-sm"
                style={{ color: "var(--cn-horror-text-muted)" }}
              >
                {rank + 1}
              </span>
              <span className="flex items-center gap-1.5 font-bold text-[13px]">
                <span
                  className="inline-block h-2.5 w-2.5 rounded-full"
                  style={{ backgroundColor: getHorrorPlayerToken(idx) }}
                />
                <span style={{ color: "var(--cn-horror-text)" }}>
                  {playerNames[player.id] ?? player.name}
                </span>
              </span>
              <span className="flex items-baseline gap-1.5">
                {delta > 0 && (
                  <span
                    className="font-extrabold text-[11px]"
                    style={{ color: "var(--cn-horror-char-gumiho)" }}
                  >
                    +{delta}
                  </span>
                )}
                <span
                  data-testid="recap-garnet-value"
                  className="min-w-[34px] text-right font-extrabold text-[17px]"
                  style={{ color: "var(--cn-horror-finished-flash)" }}
                >
                  💎 {displayed[player.id] ?? 0}
                </span>
              </span>
            </div>
          );
        })}
      </div>
    </section>
  );
}
